import React, { useState } from 'react';
import { Item, useCanvasStore } from '@/stores/canvasStore';
import { FileText, Download, Copy, MoreHorizontal } from 'lucide-react';
import { DocumentEditor } from '@/components/modals/DocumentEditor';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

interface DocumentCardProps {
  item: Item;
}

export function DocumentCard({ item }: DocumentCardProps) {
  const content = item.content as { title?: string; body?: string; wordCount?: number };
  const [isEditorOpen, setIsEditorOpen] = useState(false);

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditorOpen(true);
  };

  const wordCount = content.wordCount || 0;
  
  // Strip HTML tags and get plain text preview
  const getPlainTextPreview = (html: string | undefined) => {
    if (!html) return '';
    // Create a temporary element to parse HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;
    // Get text content (strips all HTML tags)
    const plainText = temp.textContent || temp.innerText || '';
    // Trim and get first 150 characters
    return plainText.trim().substring(0, 150);
  };
  
  const preview = getPlainTextPreview(content.body);

  const getMarkdownContent = () => {
    return `# ${content.title || 'Untitled Document'}\n\n${content.body || ''}`;
  };

  const handleCopyMarkdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(getMarkdownContent());
    toast.success('Copied to clipboard');
  };

  const handleDownloadMarkdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    const markdown = getMarkdownContent();
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${content.title || 'document'}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Downloaded');
  };

  const handleClick = (e: React.MouseEvent) => {
    // Single click in the main content area opens the editor
    // Only trigger if not clicking on the dropdown menu
    const target = e.target as HTMLElement;
    if (!target.closest('[data-radix-collection-item]') && !target.closest('button')) {
      e.stopPropagation();
      setIsEditorOpen(true);
    }
  };

  return (
    <>
      <div
        className="w-full h-full bg-card rounded-lg card-shadow overflow-hidden flex flex-col border border-border hover:border-primary/50 transition-colors group relative"
      >
        {/* Drag zones at top and bottom (20% each) - show grab cursor */}
        <div 
          className="absolute inset-x-0 top-0 h-[20%] z-20 cursor-grab"
          data-drag-zone="true"
        />
        <div 
          className="absolute inset-x-0 bottom-0 h-[20%] z-20 cursor-grab"
          data-drag-zone="true"
        />
        
        {/* Export menu - visible on hover */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-30">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7 bg-background/80 backdrop-blur-sm"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleCopyMarkdown}>
                <Copy className="mr-2 h-4 w-4" />
                Copy as Markdown
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDownloadMarkdown}>
                <Download className="mr-2 h-4 w-4" />
                Download as .md
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Content area - clickable to open editor */}
        <div 
          className="flex-1 p-4 cursor-pointer flex flex-col relative z-10"
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
        >
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground truncate flex-1 pr-8">
              {content.title || 'Untitled Document'}
            </h3>
          </div>
          
          <div className="flex-1 text-muted-foreground text-sm overflow-hidden">
            {preview ? (
              <p className="line-clamp-4">{preview}...</p>
            ) : (
              <p className="italic opacity-60">Click to edit...</p>
            )}
          </div>
          
          <div className="text-xs text-muted-foreground mt-2">
            {wordCount} words
          </div>
        </div>
      </div>
      
      <DocumentEditor
        item={item}
        isOpen={isEditorOpen}
        onClose={() => setIsEditorOpen(false)}
      />
    </>
  );
}
